import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

interface Alert {
  id: string;
  message: string;
  active: boolean;
  createdAt: Date;
}

interface AlertBannerContextType {
  alert: Alert | null;
  loading: boolean;
}

const AlertBannerContext = createContext<AlertBannerContextType | undefined>(undefined);

export function useAlertBanner() {
  const context = useContext(AlertBannerContext);
  if (context === undefined) {
    throw new Error('useAlertBanner must be used within an AlertBannerProvider');
  }
  return context;
}

interface AlertBannerProviderProps {
  children: ReactNode;
}

export function AlertBannerProvider({ children }: AlertBannerProviderProps) {
  const [alert, setAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLatestAlert = async () => {
      try {
        const alertsRef = collection(db, 'alerts');
        
        // Use simple query to avoid index requirement
        const q = query(
          alertsRef,
          where('active', '==', true),
          limit(10) // Get up to 10 active alerts
        );
        
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          // Sort manually since we can't use orderBy without index
          const alerts = querySnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate() || new Date(),
            };
          }) as Alert[];
          
          // Get the most recent alert
          const latestAlert = alerts.sort((a, b) => 
            b.createdAt.getTime() - a.createdAt.getTime()
          )[0];
          
          if (latestAlert) {
            setAlert(latestAlert);
          }
        }
      } catch (error) {
        console.warn('Error fetching alert banner:', error);
        // Fail silently for alerts as they're not critical
      } finally {
        setLoading(false);
      }
    };

    fetchLatestAlert();
  }, []);

  const value = {
    alert,
    loading
  };

  return (
    <AlertBannerContext.Provider value={value}>
      {children}
    </AlertBannerContext.Provider>
  );
}